import React, { useState } from "react";
import toast from "react-hot-toast";
import { Printer } from "react-feather";
import { PRINT_BASE, PRINT_TD, PRINT_TH, PrintHeader, printElement } from "../../../../components/common/printSheet";

/*
  Printing a Client Visit.

  Two sheets, because two different things get printed:
   - ClientVisitSheet   one visit, laid out like the paper "CLIENT VISIT REPORT"
                        pad it replaces. This is the normal print (user,
                        2026-09-18: "the details page is what users will print").
   - ClientVisitListSheet  the filtered list, for a "who visited whom this month"
                        run. Added ALONGSIDE the existing Export PDF / Export
                        Excel buttons, which are untouched.

  Deliberately faithful to the paper: the same section numbering (1. GUARDING,
  2. ALARM), the same boxes, and a real signature block at the bottom. We never
  captured a digital client signature, so the printed sheet keeps a ruled line
  for one - which is exactly what makes the printout useful on site.
*/

const RATING_LABEL = { GOOD: "Good", AVERAGE: "Average", POOR: "Poor" };
const COVERAGE_LABEL = { STANDARD: "Standard", UNDER_DEPLOYMENT: "Under Deployment" };

const fmtDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const devicesOf = (v) =>
  [
    v.hasManualAlarm ? "Manual Alarm" : null,
    v.hasAutoAlarm ? "A/Alarm" : null,
    v.hasElectricFence ? "Electric Fence" : null,
    v.hasFireAlarm ? "Fire Alarm" : null,
    v.hasCctv ? "C.C.T.V" : null,
  ].filter(Boolean);

// Batch212 - the requirement for quotation ticks.
const needsOf = (v) =>
  [
    v.needsGuarding ? "Guarding" : null,
    v.needsAlarms ? "Alarms" : null,
    v.needsOther ? "Other" : null,
  ].filter(Boolean);

const tickBox = (on) => (
  <span style={{ display: "inline-block", border: "1px solid #666", width: "13px", height: "13px", textAlign: "center", lineHeight: "13px", marginRight: "5px", fontWeight: 700 }}>
    {on ? "✓" : " "}
  </span>
);

const sectionTitle = {
  fontSize: "12px",
  fontWeight: 700,
  margin: "10px 0 4px",
  borderBottom: "1px solid #000",
  textTransform: "uppercase",
};

// "Label: ______" pairs, the way the pad is laid out.
const Pair = ({ label, value, width = "33%" }) => (
  <td style={{ ...PRINT_TD, width, borderBottom: "none" }}>
    <span style={{ fontSize: "10px", color: "#444" }}>{label}</span>
    <div style={{ fontSize: "11.5px", fontWeight: 600, borderBottom: "1px solid #999", minHeight: "15px" }}>
      {value || " "}
    </div>
  </td>
);

const Boxed = ({ label, value }) => (
  <td style={{ ...PRINT_TD, borderBottom: "none", width: "25%" }}>
    <span style={{ fontSize: "10px", color: "#444" }}>{label}</span>
    <div
      style={{
        fontSize: "13px",
        fontWeight: 700,
        border: "1px solid #666",
        textAlign: "center",
        padding: "2px 0",
        minHeight: "17px",
      }}
    >
      {value ?? " "}
    </div>
  </td>
);

/** GOOD / AVERAGE / POOR as three boxes with the chosen one ticked - as on the pad. */
const RatingRow = ({ label, value, options = ["GOOD", "AVERAGE", "POOR"], labels = RATING_LABEL }) => (
  <tr>
    <td style={{ ...PRINT_TD, width: "180px", borderBottom: "none" }}>{label}</td>
    {options.map((opt) => (
      <td key={opt} style={{ ...PRINT_TD, borderBottom: "none", width: "90px" }}>
        <span style={{ display: "inline-block", border: "1px solid #666", width: "13px", height: "13px", textAlign: "center", lineHeight: "13px", marginRight: "5px", fontWeight: 700 }}>
          {value === opt ? "✓" : " "}
        </span>
        {labels[opt] || opt}
      </td>
    ))}
  </tr>
);

const FreeText = ({ label, value }) => (
  <div style={{ marginBottom: "5px" }}>
    <div style={{ fontSize: "10px", color: "#444" }}>{label}</div>
    <div style={{ fontSize: "11.5px", border: "1px solid #999", minHeight: "26px", padding: "3px 5px", whiteSpace: "pre-wrap" }}>
      {value || " "}
    </div>
  </div>
);

/** One visit, on one A4 page. */
export const ClientVisitSheet = ({ visit }) => {
  const devices = devicesOf(visit);
  const guardInCharge = visit.guardInChargeName
    ? visit.guardInChargeEmpCode
      ? `${visit.guardInChargeEmpCode} - ${visit.guardInChargeName}`
      : visit.guardInChargeName
    : "";

  return (
    <div style={{ ...PRINT_BASE, padding: "6px" }}>
      <PrintHeader
        title="CLIENT VISIT REPORT"
        subtitle={visit.visitNumber}
        lines={[`Visited by: ${visit.visitedByName || ""}`]}
        branchCode={visit.branchCode}
      />

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            <Pair
              label={visit.isProspect ? "NAME (NEW CUSTOMER)" : "NAME"}
              value={visit.siteLabel ? `${visit.custName} - ${visit.siteLabel}` : visit.custName}
            />
            <Pair label="DATE" value={fmtDate(visit.visitedAt)} />
            <Pair label="TIME" value={fmtTime(visit.visitedAt)} />
          </tr>
          {/* Batch212: what was typed for a new customer (prospect). */}
          {visit.isProspect && (
            <tr>
              <Pair label="CUSTOMER PHONE" value={visit.prospectPhone} />
              <Pair label="AREA / LOCATION" value={visit.prospectArea} />
              <Pair label="" value="" />
            </tr>
          )}
          <tr>
            <Pair label="CONTACT PERSON" value={visit.contactPerson} />
            <Pair label="DESIGNATION" value={visit.contactDesignation} />
            <Pair label="MOB NO." value={visit.contactMobile} />
          </tr>
        </tbody>
      </table>

      <div style={sectionTitle}>Requirement (for quotation)</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          <tr>
            {["Guarding", "Alarms", "Other"].map((n) => (
              <td key={n} style={{ ...PRINT_TD, borderBottom: "none", width: "120px" }}>
                {tickBox(needsOf(visit).includes(n))}
                {n}
              </td>
            ))}
            <td style={{ ...PRINT_TD, borderBottom: "none" }} />
          </tr>
        </tbody>
      </table>
      <FreeText label="DETAILS" value={visit.requirementDetails} />

      <div style={sectionTitle}>Service &amp; details</div>

      {visit.hasOperationsChecklist ? (
        <>
          <div style={{ fontSize: "11.5px", fontWeight: 700, fontStyle: "italic", margin: "6px 0 3px" }}>1. GUARDING</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <Boxed label="NO. OF GUARDS - DAY" value={visit.guardsDay} />
                <Boxed label="NIGHT" value={visit.guardsNight} />
                <Boxed label="DOG &amp; HANDLER" value={visit.dogAndHandler} />
                <Boxed label="GUARD IN CHARGE" value={guardInCharge || null} />
              </tr>
            </tbody>
          </table>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "4px" }}>
            <tbody>
              <RatingRow label="UNIFORM" value={visit.uniformRating} />
              <RatingRow label="APPEARANCE" value={visit.appearanceRating} />
              <RatingRow label="PRESENTATION" value={visit.presentationRating} />
              <RatingRow
                label="SECURITY COVERAGE"
                value={visit.guardingCoverage}
                options={["STANDARD", "UNDER_DEPLOYMENT"]}
                labels={COVERAGE_LABEL}
              />
            </tbody>
          </table>
          <div style={{ marginTop: "5px" }}>
            <FreeText label="COMMENTS" value={visit.guardingComments} />
          </div>

          <div style={{ fontSize: "11.5px", fontWeight: 700, fontStyle: "italic", margin: "8px 0 3px" }}>2. ALARM</div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                {["Manual Alarm", "A/Alarm", "Electric Fence", "Fire Alarm", "C.C.T.V"].map((d) => (
                  <td key={d} style={{ ...PRINT_TD, borderBottom: "none" }}>
                    <span style={{ display: "inline-block", border: "1px solid #666", width: "13px", height: "13px", textAlign: "center", lineHeight: "13px", marginRight: "5px", fontWeight: 700 }}>
                      {devices.includes(d) ? "✓" : " "}
                    </span>
                    {d}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "4px" }}>
            <tbody>
              <tr>
                <Pair label="OTHERS" value={visit.alarmOthers} width="100%" />
              </tr>
            </tbody>
          </table>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "4px" }}>
            <tbody>
              <RatingRow label="QUALITY OF INSTALLATION" value={visit.installationQualityRating} />
              <RatingRow label="SECURITY COVERAGE" value={visit.alarmCoverageRating} />
            </tbody>
          </table>
          <div style={{ marginTop: "5px" }}>
            <FreeText label="COMMENT" value={visit.alarmComments} />
          </div>
        </>
      ) : (
        <div style={{ fontSize: "11px", fontStyle: "italic", margin: "6px 0" }}>
          No operations checklist was completed on this visit.
        </div>
      )}

      <FreeText label="REMARKS" value={visit.remarks} />
      <FreeText label="CLIENT REMARKS" value={visit.clientRemarks} />

      {/* The pad's own sign-off. The client signature stays a ruled line - no
          digital signature is captured, and printing an empty box labelled as
          signed would be worse than leaving room to sign. */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "14px" }}>
        <tbody>
          <tr>
            <Pair label="CHECKED BY" value={visit.visitedByName} />
            <Pair label="DATE" value={fmtDate(visit.visitedAt)} />
            <Pair label="CLIENT SIGNATURE" value="" />
          </tr>
        </tbody>
      </table>

      <div style={{ fontSize: "9px", color: "#555", marginTop: "10px" }}>
        {visit.latitude != null
          ? `Recorded at ${visit.latitude}, ${visit.longitude}${visit.accuracyMeters != null ? ` (±${Math.round(visit.accuracyMeters)} m)` : ""} · ${fmtDateTime(visit.visitedAt)}`
          : fmtDateTime(visit.visitedAt)}
      </div>
    </div>
  );
};

/** The filtered list, landscape - "who visited whom" over a date range. */
export const ClientVisitListSheet = ({ rows, filterLines, branchCode }) => (
  <div style={{ ...PRINT_BASE, padding: "6px" }}>
    <PrintHeader title="Client Visits" subtitle={`${rows.length} visit(s)`} lines={filterLines} branchCode={branchCode} />
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <th style={PRINT_TH}>Date</th>
          <th style={PRINT_TH}>Customer</th>
          <th style={PRINT_TH}>Branch</th>
          <th style={PRINT_TH}>Visited By</th>
          <th style={PRINT_TH}>Contact</th>
          <th style={{ ...PRINT_TH, textAlign: "center" }}>D/N</th>
          <th style={PRINT_TH}>Guard In Charge</th>
          <th style={PRINT_TH}>Requirement</th>
          <th style={PRINT_TH}>Remarks</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.recordGuid}>
            <td style={{ ...PRINT_TD, whiteSpace: "nowrap" }}>{fmtDateTime(r.visitedAt)}</td>
            <td style={PRINT_TD}>
              <div style={{ fontWeight: 700 }}>
                {r.custName}
                {r.isProspect ? <span style={{ fontWeight: 400, fontSize: "9.5px" }}> (new customer)</span> : null}
              </div>
              <div style={{ fontSize: "9.5px", color: "#555" }}>
                {r.visitNumber}
                {r.siteLabel ? ` · ${r.siteLabel}` : ""}
                {r.isProspect && r.prospectPhone ? ` · ${r.prospectPhone}` : ""}
                {r.isProspect && r.prospectArea ? ` · ${r.prospectArea}` : ""}
              </div>
            </td>
            <td style={PRINT_TD}>{r.branchCode}</td>
            <td style={PRINT_TD}>{r.visitedByName}</td>
            <td style={PRINT_TD}>
              {r.contactPerson || ""}
              {r.contactDesignation ? ` (${r.contactDesignation})` : ""}
            </td>
            <td style={{ ...PRINT_TD, textAlign: "center", whiteSpace: "nowrap" }}>
              {r.hasOperationsChecklist ? `${r.guardsDay ?? 0}/${r.guardsNight ?? 0}` : "—"}
            </td>
            <td style={PRINT_TD}>
              {r.guardInChargeName
                ? r.guardInChargeEmpCode
                  ? `${r.guardInChargeEmpCode} - ${r.guardInChargeName}`
                  : r.guardInChargeName
                : ""}
            </td>
            <td style={PRINT_TD}>
              {needsOf(r).join(", ")}
              {r.requirementDetails ? (
                <div style={{ fontSize: "9.5px", color: "#555" }}>{r.requirementDetails}</div>
              ) : null}
            </td>
            <td style={PRINT_TD}>{r.remarks}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Prints one visit's sheet. `compact` renders an icon-only button for a table row.
 *
 * The popup is opened synchronously inside the click - a window opened after an
 * await can be swallowed by the pop-up blocker (the lesson already written into
 * AlarmSheetPrintButton).
 */
export const ClientVisitPrintButton = ({ visit, compact = false }) => {
  const [busy, setBusy] = useState(false);

  const print = () => {
    const win = window.open("", "_blank", "width=1000,height=900");
    if (!win) {
      toast.error("Allow pop-ups for this site to print.");
      return;
    }
    setBusy(true);
    try {
      printElement(<ClientVisitSheet visit={visit} />, { title: visit.visitNumber, win });
    } catch (e) {
      win.close();
      toast.error(e?.message || "Could not prepare the print");
    } finally {
      setBusy(false);
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary p-1 lh-1"
        onClick={(e) => {
          // The row itself opens the modal - printing must not do both.
          e.stopPropagation();
          print();
        }}
        disabled={busy}
        title="Print this visit report"
      >
        <Printer size={14} />
      </button>
    );
  }

  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
      onClick={print}
      disabled={busy}
    >
      <Printer size={14} /> Print
    </button>
  );
};

/** Prints the filtered list. Sits next to the existing Export buttons, not instead of them. */
export const ClientVisitListPrintButton = ({ rows, filterLines, branchCode }) => {
  const print = () => {
    const win = window.open("", "_blank", "width=1200,height=900");
    if (!win) {
      toast.error("Allow pop-ups for this site to print.");
      return;
    }
    printElement(<ClientVisitListSheet rows={rows} filterLines={filterLines} branchCode={branchCode} />, {
      title: "Client Visits",
      page: "A4 landscape",
      win,
    });
  };

  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
      onClick={print}
      disabled={rows.length === 0}
      title={rows.length === 0 ? "Nothing to print" : "Print this list"}
    >
      <Printer size={14} /> Print
    </button>
  );
};
